import React, { useState, useEffect, useMemo } from 'react';
import { PrimaryButton, SecondaryButton, FormInput, AppIconButton } from '../ui';
import {
  Box,
  Button,
  MenuItem,
  Typography,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  Grid,
  Collapse,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  ChevronRight as ChevronRightIcon,
  CheckCircle as CheckCircleIcon,
  MergeType as MergeTypeIcon,
  TableRestaurant as TableIcon,
  Star as StarIcon,
  EventSeat as SeatIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import Modal from '../Modal';
import { useReservations } from '../../context/ReservationContext';
import { availabilityApi } from '../../api/reservationApi';
import type {
  Reservation,
  ReservationRequest,
  ReservationSource,
  TableSuggestion,
  ReservableTable,
} from '../../types/reservation.types';
import toast from 'react-hot-toast';
import { useTheme } from '../../context/ThemeContext';
import { todayLocalIso , toLocalIsoDate} from '../../utils/dateLocal';

interface ReservationModalProps {
  isOpen: boolean;
  onClose: () => void;
  reservation?: Reservation;
  preselectedDate?: string;
  preselectedTableId?: number;
}

const DURATION_OPTIONS = [
  { value: 60, label: '1 hour' },
  { value: 90, label: '1.5 hours' },
  { value: 120, label: '2 hours' },
  { value: 150, label: '2.5 hours' },
  { value: 180, label: '3 hours' },
];

const SOURCE_OPTIONS: { value: ReservationSource; label: string }[] = [
  { value: 'DASHBOARD', label: 'Dashboard' },
  { value: 'PHONE', label: 'Phone' },
  { value: 'WALKIN', label: 'Walk-in' },
];

// --- Helper types and functions ---

interface TableSection {
  sectionId: number;
  sectionName: string;
  tables: ReservableTable[];
}

interface TableFloor {
  floorId: number;
  floorName: string;
  sections: TableSection[];
  totalTables: number;
}

const groupTablesByFloorSection = (tables: ReservableTable[]): TableFloor[] => {
  const floorMap = new Map<number, TableFloor>();

  for (const table of tables) {
    const fid = table.floorId ?? 0;
    const fname = table.floorName ?? 'Main';

    if (!floorMap.has(fid)) {
      floorMap.set(fid, { floorId: fid, floorName: fname, sections: [], totalTables: 0 });
    }

    const floor = floorMap.get(fid)!;
    let section = floor.sections.find(s => s.sectionId === (table.sectionId ?? 0));
    if (!section) {
      section = { sectionId: table.sectionId ?? 0, sectionName: table.sectionName ?? 'General', tables: [] };
      floor.sections.push(section);
    }
    section.tables.push(table);
    floor.totalTables++;
  }

  return Array.from(floorMap.values());
};

type CapacityFit = 'exact' | 'good' | 'oversized' | 'undersized';

const getCapacityFit = (capacity: number, partySize: number): CapacityFit => {
  if (capacity === partySize) return 'exact';
  if (capacity > partySize && capacity <= partySize + 2) return 'good';
  if (capacity > partySize + 2) return 'oversized';
  return 'undersized';
};

const capacityFitColors: Record<CapacityFit, { bg: string; border: string; text: string; badge: string }> = {
  exact: { bg: '#f0fdf4', border: 'var(--status-ok-border)', text: '#10603C', badge: '#1E8E5A' },
  good: { bg: '#f0fdf4', border: 'var(--status-ok-border)', text: '#10603C', badge: '#1E8E5A' },
  oversized: { bg: '#fffbeb', border: 'var(--status-warn-border)', text: '#8A5411', badge: '#E0891E' },
  undersized: { bg: '#f9fafb', border: 'var(--border-subtle)', text: '#9ca3af', badge: '#9ca3af' },
};

// --- Component ---

const ReservationModal: React.FC<ReservationModalProps> = ({
  isOpen,
  onClose,
  reservation,
  preselectedDate,
  preselectedTableId,
}) => {
  const { createReservation, updateReservation, settings } = useReservations();
  const { theme } = useTheme();

  const isEdit = !!reservation;

  // Form state
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [partySize, setPartySize] = useState(2);
  const [date, setDate] = useState(preselectedDate || todayLocalIso());
  const [startTime, setStartTime] = useState('12:00');
  const [durationMinutes, setDurationMinutes] = useState(90);
  const [selectedTableIds, setSelectedTableIds] = useState<number[]>(
    preselectedTableId ? [preselectedTableId] : []
  );
  const [specialRequests, setSpecialRequests] = useState('');
  const [specialOccasionTag, setSpecialOccasionTag] = useState('');
  const [guestNotes, setGuestNotes] = useState('');
  const [showOptionalFields, setShowOptionalFields] = useState(false);
  const [source, setSource] = useState<ReservationSource>('DASHBOARD');

  // Available tables and suggestions
  const [availableTables, setAvailableTables] = useState<ReservableTable[]>([]);
  const [tableSuggestions, setTableSuggestions] = useState<TableSuggestion[]>([]);
  const [loadingTables, setLoadingTables] = useState(false);
  const [availabilityChecked, setAvailabilityChecked] = useState(false);

  // Floor collapse state
  const [collapsedFloors, setCollapsedFloors] = useState<Set<number>>(new Set());

  // Loading and error
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Populate form when editing
  useEffect(() => {
    if (reservation) {
      setCustomerName(reservation.customerName);
      setCustomerPhone(reservation.customerPhone);
      setCustomerEmail(reservation.customerEmail || '');
      setPartySize(reservation.partySize);
      setDate(reservation.reservationDate);
      setStartTime(reservation.startTime);
      setDurationMinutes(reservation.durationMinutes);
      setSelectedTableIds(reservation.tableIds || (reservation.tableId ? [reservation.tableId] : []));
      setSpecialRequests(reservation.specialRequests || '');
      setSpecialOccasionTag(reservation.specialOccasionTag || '');
      setGuestNotes(reservation.guestNotes || '');
      setSource(reservation.source);
      // Auto-expand optional fields if they have data
      if (reservation.specialOccasionTag || reservation.guestNotes || reservation.specialRequests) {
        setShowOptionalFields(true);
      }
    }
  }, [reservation]);

  // Generate time slots
  const generateTimeSlots = () => {
    const slots: string[] = [];
    const interval = settings?.timeSlotIntervalMinutes || 30;
    const opening = settings?.openingTime || '10:00';
    const closing = settings?.closingTime || '22:00';

    const [openHour, openMin] = opening.split(':').map(Number);
    const [closeHour, closeMin] = closing.split(':').map(Number);

    let currentMinutes = openHour * 60 + openMin;
    const endMinutes = closeHour * 60 + closeMin;

    while (currentMinutes < endMinutes) {
      const hours = Math.floor(currentMinutes / 60);
      const mins = currentMinutes % 60;
      slots.push(`${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`);
      currentMinutes += interval;
    }

    return slots;
  };

  // Calculate end time
  const calculateEndTime = () => {
    const [hours, mins] = startTime.split(':').map(Number);
    const totalMinutes = hours * 60 + mins + durationMinutes;
    const endHours = Math.floor(totalMinutes / 60) % 24;
    const endMins = totalMinutes % 60;
    return `${endHours.toString().padStart(2, '0')}:${endMins.toString().padStart(2, '0')}`;
  };

  // Check availability when date/time/party size changes
  useEffect(() => {
    const checkAvailability = async () => {
      if (!date || !startTime || partySize < 1) return;

      setLoadingTables(true);
      setAvailabilityChecked(false);

      try {
        const endTime = calculateEndTime();

        const [tables, suggestions] = await Promise.all([
          availabilityApi.getTables(date, startTime, endTime),
          availabilityApi.suggestTables(date, startTime, endTime, partySize),
        ]);

        setAvailableTables(tables);
        setTableSuggestions(suggestions);
        setAvailabilityChecked(true);

        // Auto-select first suggestion if no table selected
        if (selectedTableIds.length === 0 && suggestions.length > 0 && !suggestions[0].merged) {
          setSelectedTableIds([suggestions[0].tables[0].id]);
        }
      } catch (err) {
        console.error('Error checking availability:', err);
      } finally {
        setLoadingTables(false);
      }
    };

    const debounce = setTimeout(checkAvailability, 500);
    return () => clearTimeout(debounce);
  }, [date, startTime, durationMinutes, partySize]);

  const toggleTableSelection = (tableId: number) => {
    if (needsMerge) {
      // Multi-select: toggle add/remove
      setSelectedTableIds((prev) =>
        prev.includes(tableId)
          ? prev.filter((id) => id !== tableId)
          : [...prev, tableId]
      );
    } else {
      // Single-select: clicking a new table replaces the old one
      setSelectedTableIds((prev) =>
        prev.includes(tableId) ? [] : [tableId]
      );
    }
  };

  const selectSuggestion = (suggestion: TableSuggestion) => {
    setSelectedTableIds(suggestion.tables.map((t) => t.id));
  };

  const getTotalCapacity = () => {
    return availableTables
      .filter((t) => selectedTableIds.includes(t.id))
      .reduce((sum, t) => sum + t.seatingCapacity, 0);
  };

  const toggleFloor = (floorId: number) => {
    setCollapsedFloors(prev => {
      const next = new Set(prev);
      if (next.has(floorId)) {
        next.delete(floorId);
      } else {
        next.add(floorId);
      }
      return next;
    });
  };

  // Grouped tables
  const groupedTables = useMemo(() => groupTablesByFloorSection(availableTables), [availableTables]);
  const hasMultipleFloors = groupedTables.length > 1;
  const largestSingleCapacity = useMemo(
    () => availableTables.reduce((max, t) => Math.max(max, t.seatingCapacity), 0),
    [availableTables]
  );
  const needsMerge = partySize > largestSingleCapacity && availableTables.length > 0;

  // Sort suggestions: exact fit first, then slightly larger, then merge options
  const sortedSuggestions = useMemo(() => {
    return [...tableSuggestions].sort((a, b) => {
      // Non-merged before merged
      if (a.merged !== b.merged) return a.merged ? 1 : -1;
      // Closer total capacity to party size first
      const aDiff = Math.abs(a.totalCapacity - partySize);
      const bDiff = Math.abs(b.totalCapacity - partySize);
      return aDiff - bDiff;
    });
  }, [tableSuggestions, partySize]);

  const handleSubmit = async () => {
    // Validation
    if (!customerName.trim()) {
      setError('Customer name is required');
      return;
    }
    if (!customerPhone.trim()) {
      setError('Phone number is required');
      return;
    }
    if (partySize < 1) {
      setError('Party size must be at least 1');
      return;
    }
    if (selectedTableIds.length === 0) {
      setError('Please select at least one table');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data: ReservationRequest = {
        customerName: customerName.trim(),
        customerPhone: customerPhone.trim(),
        customerEmail: customerEmail.trim() || undefined,
        partySize,
        date,
        startTime,
        durationMinutes,
        tableId: selectedTableIds.length === 1 ? selectedTableIds[0] : undefined,
        tableIds: selectedTableIds.length > 1 ? selectedTableIds : undefined,
        specialRequests: specialRequests.trim() || undefined,
        specialOccasionTag: specialOccasionTag || undefined,
        guestNotes: guestNotes.trim() || undefined,
        source,
      };

      if (isEdit && reservation) {
        await updateReservation(reservation.id, data);
        toast.success('Reservation updated');
      } else {
        await createReservation(data);
        toast.success('Reservation created');
      }

      onClose();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to save reservation');
    } finally {
      setLoading(false);
    }
  };

  const timeSlots = generateTimeSlots();

  // --- Render helpers ---

  const getTableLocationPath = (table: ReservableTable) => {
    const parts: string[] = [];
    if (table.floorName && hasMultipleFloors) parts.push(table.floorName);
    if (table.sectionName) parts.push(table.sectionName);
    return parts.join(' > ');
  };

  const renderRecommendationCard = (suggestion: TableSuggestion, idx: number) => {
    const isSelected =
      suggestion.tables.length === selectedTableIds.length &&
      suggestion.tables.every((t) => selectedTableIds.includes(t.id));
    const isMerge = suggestion.merged;
    const isExactFit = suggestion.totalCapacity === partySize;
    const isCloseFit = !isExactFit && suggestion.totalCapacity >= partySize && suggestion.totalCapacity <= partySize + 2;
    const tableNames = suggestion.tables.map(t => t.name).join(' + ');
    const locationPath = suggestion.tables[0] ? getTableLocationPath(suggestion.tables[0]) : '';

    // Badge differentiation
    let badgeLabel: string;
    let badgeBg: string;
    let badgeColor: string;
    if (isMerge) {
      badgeLabel = 'MERGE';
      badgeBg = '#dbeafe';
      badgeColor = '#1d4ed8';
    } else if (idx === 0 && (isExactFit || isCloseFit)) {
      badgeLabel = 'BEST FIT';
      badgeBg = '#dcfce7';
      badgeColor = '#15803d';
    } else if (isExactFit || isCloseFit) {
      badgeLabel = 'ALSO FITS';
      badgeBg = '#dcfce7';
      badgeColor = '#166534';
    } else {
      badgeLabel = 'GOOD FIT';
      badgeBg = '#fef3c7';
      badgeColor = '#92400e';
    }

    const iconBg = isMerge ? '#dbeafe' : (isExactFit || isCloseFit ? '#dcfce7' : '#f3f4f6');

    return (
      <Box
        key={idx}
        onClick={() => selectSuggestion(suggestion)}
        sx={{
          p: 1.5,
          borderRadius: 2,
          border: 2,
          borderColor: isSelected ? 'var(--action)' : ((isExactFit || isCloseFit) ? '#22c55e' : '#e5e7eb'),
          bgcolor: isSelected ? '#F0FDFA' : ((isExactFit || isCloseFit) ? '#f0fdf4' : '#fff'),
          cursor: 'pointer',
          transition: 'all 0.15s',
          '&:hover': {
            borderColor: isSelected ? 'var(--action)' : 'var(--action)',
            bgcolor: isSelected ? '#F0FDFA' : '#f8fafc',
          },
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        {/* Icon */}
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: iconBg,
            flexShrink: 0,
          }}
        >
          {isMerge ? (
            <MergeTypeIcon sx={{ fontSize: 18, color: 'var(--action)' }} />
          ) : (isExactFit || isCloseFit) ? (
            <StarIcon sx={{ fontSize: 18, color: 'var(--status-ok)' }} />
          ) : (
            <TableIcon sx={{ fontSize: 18, color: 'var(--text-tertiary)' }} />
          )}
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {tableNames}
            </Typography>
            <Box
              component="span"
              sx={{
                px: 0.75,
                py: 0.15,
                borderRadius: 1,
                fontSize: '0.65rem',
                fontWeight: 700,
                letterSpacing: '0.02em',
                bgcolor: badgeBg,
                color: badgeColor,
              }}
            >
              {badgeLabel}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.25 }}>
            <Typography variant="caption" sx={{ color: 'var(--text-tertiary)' }}>
              {suggestion.totalCapacity} seats
            </Typography>
            {locationPath && (
              <Typography variant="caption" sx={{ color: 'var(--text-tertiary)' }}>
                {locationPath}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Selection indicator */}
        {isSelected && (
          <CheckCircleIcon sx={{ fontSize: 20, color: 'var(--action)', flexShrink: 0 }} />
        )}
      </Box>
    );
  };

  const renderTableCard = (table: ReservableTable) => {
    const isSelected = selectedTableIds.includes(table.id);
    const fit = getCapacityFit(table.seatingCapacity, partySize);
    const colors = capacityFitColors[fit];

    // Check if this table is the best fit among all available
    const isBestFit = sortedSuggestions.length > 0 &&
      !sortedSuggestions[0].merged &&
      sortedSuggestions[0].tables.length === 1 &&
      sortedSuggestions[0].tables[0].id === table.id;

    return (
      <Box
        key={table.id}
        onClick={() => toggleTableSelection(table.id)}
        sx={{
          p: 1.5,
          borderRadius: 2,
          border: 2,
          borderColor: isSelected ? 'var(--action)' : colors.border,
          bgcolor: isSelected ? '#F0FDFA' : colors.bg,
          cursor: 'pointer',
          transition: 'all 0.15s',
          opacity: fit === 'undersized' && !isSelected ? 0.6 : 1,
          position: 'relative',
          '&:hover': {
            borderColor: isSelected ? 'var(--action)' : 'var(--action)',
            opacity: 1,
          },
        }}
      >
        {isBestFit && !isSelected && (
          <Box
            sx={{
              position: 'absolute',
              top: -8,
              right: 8,
              px: 0.75,
              py: 0.15,
              borderRadius: 1,
              fontSize: '0.6rem',
              fontWeight: 700,
              bgcolor: 'var(--action)',
              color: 'var(--text-inverse)',
              letterSpacing: '0.02em',
            }}
          >
            BEST FIT
          </Box>
        )}
        <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
          {table.name}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 0.5 }}>
          <Typography
            variant="caption"
            sx={{
              color: colors.text,
              fontWeight: 500,
              display: 'flex',
              alignItems: 'center',
              gap: 0.25,
            }}
          >
            <SeatIcon sx={{ fontSize: 12 }} />
            {table.seatingCapacity}
          </Typography>
          {isSelected && (
            <CheckCircleIcon sx={{ fontSize: 14, color: 'var(--action)' }} />
          )}
        </Box>
      </Box>
    );
  };

  const renderSingleSelectSummary = () => {
    if (selectedTableIds.length !== 1) return null;
    const table = availableTables.find(t => t.id === selectedTableIds[0]);
    if (!table) return null;
    const loc = getTableLocationPath(table);

    return (
      <Box
        sx={{
          mt: 2,
          p: 1.5,
          borderRadius: 2,
          bgcolor: 'var(--status-ok-bg)',
          border: 1,
          borderColor: '#bbf7d0', /* basil-todo */
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <CheckCircleIcon sx={{ fontSize: 16, color: 'var(--status-ok)', flexShrink: 0 }} />
        <Typography variant="body2" sx={{ fontWeight: 500, color: 'var(--status-ok-text)', flex: 1 }}>
          {table.name} selected ({table.seatingCapacity} seats)
          {loc && (
            <Typography component="span" sx={{ color: 'var(--status-ok-text)', fontSize: '0.8rem', ml: 0.5 }}>
              — {loc}
            </Typography>
          )}
        </Typography>
        <Chip
          label="Remove"
          size="small"
          onDelete={() => toggleTableSelection(table.id)}
          sx={{ height: 24, fontSize: '0.7rem' }}
        />
      </Box>
    );
  };

  const renderCapacityBar = () => {
    const totalCapacity = getTotalCapacity();
    const percentage = Math.min(100, (totalCapacity / partySize) * 100);
    const isMet = totalCapacity >= partySize;
    const remaining = partySize - totalCapacity;
    const isOverSelected = totalCapacity > partySize * 2;

    const selectedTables = availableTables.filter(t => selectedTableIds.includes(t.id));

    return (
      <Box
        sx={{
          mt: 2,
          p: 2,
          borderRadius: 2,
          bgcolor: isOverSelected ? '#fffbeb' : (isMet ? '#f0fdf4' : '#fffbeb'),
          border: 1,
          borderColor: isOverSelected ? '#fcd34d' : (isMet ? '#bbf7d0' : '#fde68a'),
        }}
      >
        {/* Selected tables as removable chips */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.5 }}>
          {selectedTables.map(t => {
            const loc = getTableLocationPath(t);
            return (
              <Chip
                key={t.id}
                label={
                  <>
                    {t.name}
                    {loc && (
                      <Typography component="span" sx={{ color: 'var(--text-tertiary)', fontSize: '0.65rem', ml: 0.5 }}>
                        {loc}
                      </Typography>
                    )}
                  </>
                }
                onDelete={() => toggleTableSelection(t.id)}
                size="small"
                sx={{
                  height: 26,
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  bgcolor: 'var(--bg-surface)',
                  border: 1,
                  borderColor: 'var(--border-subtle)',
                }}
              />
            );
          })}
        </Box>

        {/* Capacity bar */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ flex: 1, height: 8, borderRadius: 4, bgcolor: 'var(--bg-sunken)', overflow: 'hidden' }}>
            <Box
              sx={{
                width: `${percentage}%`,
                height: '100%',
                borderRadius: 4,
                bgcolor: isOverSelected ? '#f59e0b' : (isMet ? '#22c55e' : '#f59e0b'),
                transition: 'width 0.3s ease',
              }}
            />
          </Box>
          <Typography variant="caption" sx={{ fontWeight: 600, color: isOverSelected ? '#92400e' : (isMet ? '#15803d' : '#92400e'), whiteSpace: 'nowrap' }}>
            {totalCapacity}/{partySize} seats
          </Typography>
        </Box>

        {/* Status message */}
        <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {isOverSelected ? (
            <>
              <WarningIcon sx={{ fontSize: 14, color: 'var(--status-warn)' }} />
              <Typography variant="caption" sx={{ color: 'var(--status-warn-text)', fontWeight: 500 }}>
                More tables selected than needed. Consider removing some.
              </Typography>
            </>
          ) : isMet ? (
            <>
              <CheckCircleIcon sx={{ fontSize: 14, color: 'var(--status-ok)' }} />
              <Typography variant="caption" sx={{ color: 'var(--status-ok-text)', fontWeight: 500 }}>
                Capacity met — ready to reserve
              </Typography>
            </>
          ) : (
            <Typography variant="caption" sx={{ color: 'var(--status-warn-text)', fontWeight: 500 }}>
              Select {remaining} more seat{remaining !== 1 ? 's' : ''} to seat all guests
            </Typography>
          )}
        </Box>
      </Box>
    );
  };

  const renderEmptyState = () => {
    if (tableSuggestions.length > 0) {
      return (
        <Box sx={{ textAlign: 'center', py: 3 }}>
          <Typography variant="body2" sx={{ color: 'var(--text-tertiary)', mb: 1 }}>
            No single table seats {partySize} guests, but these combinations work:
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <TableIcon sx={{ fontSize: 40, color: '#d1d5db', mb: 1 }} /> {/* basil-todo */}
        <Typography variant="body2" sx={{ color: 'var(--text-tertiary)', fontWeight: 500 }}>
          No tables available for this time slot
        </Typography>
        <Typography variant="caption" sx={{ color: 'var(--text-tertiary)', mt: 0.5, display: 'block' }}>
          Try a different time or date
        </Typography>
      </Box>
    );
  };

  const renderTableSection = () => {
    if (!availabilityChecked && !loadingTables) return null;

    // Empty state: no tables and no suggestions
    if (availableTables.length === 0 && tableSuggestions.length === 0 && availabilityChecked) {
      return renderEmptyState();
    }

    // No single table fits but merge suggestions exist
    const showMergeOnlyHeader = availableTables.length === 0 && tableSuggestions.length > 0;

    return (
      <>
        {/* Merge helper text */}
        {needsMerge && availableTables.length > 0 && (
          <Box sx={{
            mb: 2,
            px: 1.5,
            py: 1,
            borderRadius: 1.5,
            bgcolor: '#f0f9ff', /* basil-todo */
            border: 1,
            borderColor: '#bae6fd', /* basil-todo */
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}>
            <MergeTypeIcon sx={{ fontSize: 16, color: '#0284c7' }} /> {/* basil-todo */}
            <Typography variant="caption" sx={{ color: '#0369a1' }}> {/* basil-todo */}
              No single table seats {partySize}. Select multiple tables to combine capacity.
            </Typography>
          </Box>
        )}

        {showMergeOnlyHeader && renderEmptyState()}

        {/* Recommendation Cards */}
        {sortedSuggestions.length > 0 && (
          <Box sx={{ mb: 2.5 }}>
            <Typography variant="caption" sx={{ fontWeight: 600, color: 'var(--text-secondary)', mb: 1, display: 'block', textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.65rem' }}>
              Recommended for {partySize} guests
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {sortedSuggestions.slice(0, 3).map((suggestion, idx) =>
                renderRecommendationCard(suggestion, idx)
              )}
            </Box>
          </Box>
        )}

        {/* Floor/Section grouped tables */}
        {availableTables.length > 0 && (
          <Box>
              <Typography variant="caption" sx={{ fontWeight: 600, color: 'var(--text-secondary)', mb: 1, display: 'block', textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.65rem' }}>
                All available tables
              </Typography>

              {groupedTables.map(floor => {
                const isCollapsed = collapsedFloors.has(floor.floorId);

                // Skip floor header if only one floor
                if (!hasMultipleFloors) {
                  return (
                    <Box key={floor.floorId}>
                      {floor.sections.map(section => (
                        <Box key={section.sectionId} sx={{ mb: 2 }}>
                          {floor.sections.length > 1 && (
                            <Typography
                              variant="caption"
                              sx={{ color: 'var(--text-tertiary)', fontWeight: 500, mb: 0.75, display: 'block' }}
                            >
                              {section.sectionName}
                              <Typography component="span" sx={{ color: 'var(--text-tertiary)', fontSize: '0.7rem', ml: 0.75 }}>
                                {section.tables.length} table{section.tables.length !== 1 ? 's' : ''}
                              </Typography>
                            </Typography>
                          )}
                          <Box sx={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(90px, 1fr))',
                            gap: 1,
                          }}>
                            {section.tables.map(renderTableCard)}
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  );
                }

                return (
                  <Box key={floor.floorId} sx={{ mb: 2, border: 1, borderColor: 'var(--border-subtle)', borderRadius: 2, overflow: 'hidden' }}>
                    <Box
                      onClick={() => toggleFloor(floor.floorId)}
                      sx={{
                        px: 1.5, py: 1, bgcolor: 'var(--bg-sunken)', cursor: 'pointer',
                        display: 'flex', alignItems: 'center', gap: 0.5,
                        '&:hover': { bgcolor: 'var(--bg-sunken)' },
                      }}
                    >
                      {isCollapsed ? (
                        <ChevronRightIcon sx={{ fontSize: 18, color: 'var(--text-tertiary)' }} />
                      ) : (
                        <ExpandMoreIcon sx={{ fontSize: 18, color: 'var(--text-tertiary)' }} />
                      )}
                      <Typography variant="body2" sx={{ fontWeight: 600, flex: 1 }}>
                        {floor.floorName}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'var(--text-tertiary)' }}>
                        {floor.totalTables} table{floor.totalTables !== 1 ? 's' : ''}
                      </Typography>
                    </Box>

                    <Collapse in={!isCollapsed}>
                      <Box sx={{ p: 1.5 }}>
                        {floor.sections.map(section => (
                          <Box key={section.sectionId} sx={{ mb: 1.5, '&:last-child': { mb: 0 } }}>
                            <Typography
                              variant="caption"
                              sx={{ color: 'var(--text-tertiary)', fontWeight: 500, mb: 0.75, display: 'block' }}
                            >
                              {section.sectionName}
                              <Typography component="span" sx={{ color: 'var(--text-tertiary)', fontSize: '0.7rem', ml: 0.75 }}>
                                {section.tables.length} table{section.tables.length !== 1 ? 's' : ''}
                              </Typography>
                            </Typography>
                            <Box sx={{
                              display: 'grid',
                              gridTemplateColumns: 'repeat(auto-fill, minmax(90px, 1fr))',
                              gap: 1,
                            }}>
                              {section.tables.map(renderTableCard)}
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    </Collapse>
                  </Box>
                );
              })}
            </Box>
        )}

        {/* Selection Summary */}
        {selectedTableIds.length > 0 && (
          !needsMerge && selectedTableIds.length === 1
            ? renderSingleSelectSummary()
            : renderCapacityBar()
        )}
      </>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? 'Edit Reservation' : 'Create Reservation'}
      maxWidth="lg"
    >
      <Box className="space-y-6">
        {error && (
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Customer Details */}
        <Box>
          <Typography variant="subtitle2" className="font-semibold mb-3">
            Customer Details
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormInput
                label="Customer Name"
                fullWidth
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormInput
                label="Phone Number"
                fullWidth
                value={customerPhone}
                onChange={(e) => setCustomerPhone(e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <FormInput
                label="Email (optional)"
                fullWidth
                type="email"
                value={customerEmail}
                onChange={(e) => setCustomerEmail(e.target.value)}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Party Size */}
        <Box>
          <Typography variant="subtitle2" className="font-semibold mb-3">
            Party Size
          </Typography>
          <Box className="flex items-center gap-4">
            <AppIconButton
              icon={<RemoveIcon />}
              tooltip="Decrease party size"
              variant="utility"
              size="small"
              onClick={() => setPartySize((p) => Math.max(1, p - 1))}
              sx={{ border: '1px solid var(--border-subtle)' }}
            />
            <Typography variant="h5" className="w-12 text-center font-semibold">
              {partySize}
            </Typography>
            <AppIconButton
              icon={<AddIcon />}
              tooltip="Increase party size"
              variant="utility"
              size="small"
              onClick={() => setPartySize((p) => Math.min(settings?.maxPartySize || 20, p + 1))}
              sx={{ border: '1px solid var(--border-subtle)' }}
            />
            <Typography variant="body2" color="textSecondary">
              guests
            </Typography>
          </Box>
        </Box>

        {/* Date & Time */}
        <Box>
          <Typography variant="subtitle2" className="font-semibold mb-3">
            Date & Time
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <FormInput
                label="Date"
                type="date"
                fullWidth
                value={date}
                onChange={(e) => setDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  min: todayLocalIso(),
                  ...(settings?.maxAdvanceBookingDays && {
                    max: toLocalIsoDate(new Date(Date.now() + settings.maxAdvanceBookingDays * 86400000)),
                  }),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Time</InputLabel>
                <Select
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  label="Time"
                >
                  {timeSlots.map((slot) => (
                    <MenuItem key={slot} value={slot}>
                      {slot}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Duration</InputLabel>
                <Select
                  value={durationMinutes}
                  onChange={(e) => setDurationMinutes(Number(e.target.value))}
                  label="Duration"
                >
                  {DURATION_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Box>

        {/* Table Selection */}
        <Box>
          <Typography variant="subtitle2" className="font-semibold mb-3">
            Select Table(s)
          </Typography>

          {loadingTables ? (
            <Box className="flex items-center justify-center py-8">
              <CircularProgress size={24} />
              <Typography className="ml-2">Checking availability...</Typography>
            </Box>
          ) : (
            renderTableSection()
          )}
        </Box>

        {/* Optional Fields Toggle */}
        <Box
          onClick={() => setShowOptionalFields(!showOptionalFields)}
          sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', py: 1, px: 0.5, borderRadius: 1, '&:hover': { backgroundColor: theme.gray?.[100] } }}
        >
          {showOptionalFields ? <ExpandLessIcon sx={{ fontSize: 20, color: theme.gray?.[500] }} /> : <ExpandMoreIcon sx={{ fontSize: 20, color: theme.gray?.[500] }} />}
          <Typography variant="subtitle2" sx={{ color: theme.gray?.[600], fontWeight: 600 }}>
            Occasion, Notes & Preferences
          </Typography>
          {(specialOccasionTag || guestNotes) && (
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: theme.primary, flexShrink: 0 }} />
          )}
        </Box>

        <Collapse in={showOptionalFields}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>

        {/* Special Occasion Tag */}
        <Box>
          <Typography variant="subtitle2" className="font-semibold mb-3" sx={{ color: theme.text }}>
            Special Occasion
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {[
              { value: '', label: 'None' },
              { value: 'BIRTHDAY', label: 'Birthday' },
              { value: 'ANNIVERSARY', label: 'Anniversary' },
              { value: 'BUSINESS_DINNER', label: 'Business Dinner' },
              { value: 'DATE_NIGHT', label: 'Date Night' },
              { value: 'CELEBRATION', label: 'Celebration' },
              { value: 'OTHER', label: 'Other' },
            ].map((tag) => (
              <Chip
                key={tag.value}
                label={tag.label}
                onClick={() => setSpecialOccasionTag(tag.value)}
                variant={specialOccasionTag === tag.value ? 'filled' : 'outlined'}
                size="small"
                sx={{
                  cursor: 'pointer',
                  ...(specialOccasionTag === tag.value
                    ? { backgroundColor: theme.primary, color: 'var(--text-inverse)', '&:hover': { backgroundColor: theme.hover } }
                    : { borderColor: theme.gray?.[300], color: theme.text, '&:hover': { backgroundColor: theme.gray?.[100] } }),
                }}
              />
            ))}
          </Box>
        </Box>

        {/* Guest Notes / Preferences */}
        <Box>
          <Typography variant="subtitle2" className="font-semibold mb-3" sx={{ color: theme.text }}>
            Guest Notes & Preferences
          </Typography>
          <FormInput
            fullWidth
            multiline
            rows={2}
            placeholder="Allergies, dietary restrictions, high chair needed, window table preferred, accessibility needs..."
            value={guestNotes}
            onChange={(e) => setGuestNotes(e.target.value)}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: theme.components.input.background,
                '& fieldset': { borderColor: theme.components.input.border },
                '&:hover fieldset': { borderColor: theme.primary },
                '&.Mui-focused fieldset': { borderColor: theme.components.input.focus },
              },
            }}
          />
        </Box>

        {/* Special Requests */}
        <Box>
          <Typography variant="subtitle2" className="font-semibold mb-3">
            Special Requests
          </Typography>
          <FormInput
            fullWidth
            multiline
            rows={2}
            placeholder="Flowers on table, surprise cake, specific music..."
            value={specialRequests}
            onChange={(e) => setSpecialRequests(e.target.value)}
          />
        </Box>

        {/* Source */}
        <Box>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Source</InputLabel>
            <Select
              value={source}
              onChange={(e) => setSource(e.target.value as ReservationSource)}
              label="Source"
            >
              {SOURCE_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        </Box>
        </Collapse>

        {/* Actions */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, pt: 2, borderTop: 1, borderColor: 'divider', flexDirection: { xs: 'column-reverse', sm: 'row' } }}>
          <SecondaryButton onClick={onClose} disabled={loading} sx={{ width: { xs: '100%', sm: 'auto' } }}>
            Cancel
          </SecondaryButton>
          <PrimaryButton
            onClick={handleSubmit}
            disabled={loading || selectedTableIds.length === 0}
            loading={loading}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            {isEdit ? 'Update Reservation' : 'Create Reservation'}
          </PrimaryButton>
        </Box>
      </Box>
    </Modal>
  );
};

export default ReservationModal;
